import type { ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Link } from "wouter";
import { ACCENTS, type AccentName } from "../../lib/accents";
import { cn } from "../../lib/cn";
import { FOCUS_RING } from "../../lib/focusRing";
import { Card } from "./Card";
import { Skeleton } from "./Skeleton";

export interface StatTileProps {
  icon: LucideIcon;
  accent: AccentName;
  label: string;
  value: ReactNode;
  sublabel?: string;
  /** Calm, neutral look for a zero/resting figure, so the actionable tiles stand out. */
  muted?: boolean;
  /** Skeleton the value and sublabel while the figure loads (also disables the tile). */
  loading?: boolean;
  /** Spoken label: the visible content is a bare number and a short noun. */
  actionLabel: string;
  /** Opens a detail view (a dialog, usually). Give either this or `href`. */
  onSelect?: () => void;
  /** Navigates instead. */
  href?: string;
  className?: string;
}

/**
 * One "at a glance" figure (docs/v2-core/STYLE.md): a glass tile with a big
 * Fredoka number and a round accent pebble. A lit tile takes its accent
 * wash; a muted one stays plain glass. No top colour strip, no tinted box.
 * Wrap a row of them in a <ul> and each in an <li>.
 */
export function StatTile({
  icon: Icon,
  accent,
  label,
  value,
  sublabel,
  muted = false,
  loading = false,
  actionLabel,
  onSelect,
  href,
  className,
}: StatTileProps) {
  const a = ACCENTS[accent];
  const calm = muted || loading;

  const body = (
    <>
      <div className="flex items-start justify-between gap-2">
        {loading ? (
          <Skeleton className="h-8 w-10" />
        ) : (
          <p className="font-display text-3xl font-bold leading-none tabular-nums text-neutral-900">{value}</p>
        )}
        <span
          className={cn(
            "inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full ring-4 ring-white/70",
            calm ? "bg-neutral-100 text-neutral-500" : a.chip,
          )}
        >
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
      </div>
      {/* mt-auto: a tile stretched taller than its content (the dashboard's
          column beside Recent activity) keeps its label at the bottom. */}
      <div className="mt-auto pt-2">
        <p className="text-sm font-medium text-neutral-700">{label}</p>
        {loading ? (
          <Skeleton className="mt-1 h-3 w-16" />
        ) : (
          sublabel && <p className="mt-0.5 text-xs text-neutral-600">{sublabel}</p>
        )}
      </div>
    </>
  );

  const tileClass = cn(
    "relative flex h-full w-full flex-col overflow-hidden p-4 text-left sm:p-5",
    !calm && a.wash,
    FOCUS_RING,
    className,
  );

  if (href && !loading) {
    return (
      <Card asChild interactive className={tileClass}>
        <Link href={href} aria-label={actionLabel}>
          {body}
        </Link>
      </Card>
    );
  }
  return (
    <Card asChild interactive={!loading && Boolean(onSelect)} className={tileClass}>
      <button
        type="button"
        onClick={onSelect}
        disabled={loading || !onSelect}
        aria-label={actionLabel}
        className="disabled:cursor-default"
      >
        {body}
      </button>
    </Card>
  );
}
